/*
 * Copyright (c) 2023 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { enGB, de, he, it, nl } from "date-fns/locale";
import { useMemo } from "react";

import { useCommonState } from "common/hooks";

const SUPPORTED_LOCALES = ["de", "en", "he", "it", "nl"];
const DEFAULT_SUPPORTED_LOCALE = "en-US";

export const useLocale = () => {
    const { configuration } = useCommonState();
    const { isDriverApp } = configuration;

    const appLocale = useMemo(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const language = urlParams.get("language");
        return (
            SUPPORTED_LOCALES.find((supportedLocale) => language?.startsWith(supportedLocale)) ||
            DEFAULT_SUPPORTED_LOCALE
        );
    }, []);

    const supportedLocale = isDriverApp
        ? appLocale
        : navigator.languages.find((language) =>
              SUPPORTED_LOCALES.find((supportedLocale) => language.startsWith(supportedLocale))
          ) || DEFAULT_SUPPORTED_LOCALE;

    const lang = supportedLocale.split("-")[0];

    const locale: Locale = useMemo(() => {
        switch (lang) {
            case "en":
                return enGB;
            case "de":
                return de;
            case "he":
                return he;
            case "it":
                return it;
            case "nl":
                return nl;
            default:
                return enGB;
        }
    }, [lang]);

    return {
        locale,
        supportedLocale,
        lang,
    };
};
